// src/components/audio/audioFormats.js
//
// Single source of truth for audio file format support across DataQuad's
// audio surfaces (AudioPlayer, AudioAudit detection, future inline cell players).
//
// Each entry maps an extension to:
//   - mime:        the Blob/HTML5 audio MIME type
//   - reliable:    true if all major browsers (incl. Chromium WebView used by
//                  Tauri on Windows) can play it natively. false = playback
//                  may fail and we should warn.
//   - description: human-readable name shown in tooltips/errors.
//
// To add a new format:
//   1. Add it to AUDIO_FORMATS below.
//   2. AUDIO_EXT_REGEX and getMimeType() update automatically.

export const AUDIO_FORMATS = {
  // Most common in survey work
  m4a:  { mime: 'audio/mp4',                reliable: true,  description: 'MPEG-4 Audio (m4a)' },
  mp3:  { mime: 'audio/mpeg',               reliable: true,  description: 'MP3' },
  mp4:  { mime: 'audio/mp4',                reliable: true,  description: 'MPEG-4 (mp4 audio)' },
  wav:  { mime: 'audio/wav',                reliable: true,  description: 'WAV (uncompressed)' },
  ogg:  { mime: 'audio/ogg',                reliable: true,  description: 'Ogg Vorbis' },
  oga:  { mime: 'audio/ogg',                reliable: true,  description: 'Ogg Audio' },
  opus: { mime: 'audio/ogg; codecs=opus',   reliable: true,  description: 'Opus (Ogg container)' },
  webm: { mime: 'audio/webm',               reliable: true,  description: 'WebM Audio' },
  aac:  { mime: 'audio/aac',                reliable: true,  description: 'AAC' },
  flac: { mime: 'audio/flac',               reliable: true,  description: 'FLAC (lossless)' },

  // Mobile / low-bandwidth survey formats — Android voice memo apps
  // and some SurveyCTO configurations on older devices use these.
  amr:  { mime: 'audio/amr',                reliable: true,  description: 'AMR (Adaptive Multi-Rate)' },
  '3gp':  { mime: 'audio/3gpp',             reliable: true,  description: '3GPP Audio' },
  '3gpp': { mime: 'audio/3gpp',             reliable: true,  description: '3GPP Audio' },

  // Patchy browser support — we'll warn the user when these are encountered.
  // The file may still play if WebView2 has the codec installed.
  wma:  { mime: 'audio/x-ms-wma',           reliable: false, description: 'Windows Media Audio (limited browser support)' },
  aiff: { mime: 'audio/aiff',               reliable: false, description: 'AIFF (limited browser support)' },
  aif:  { mime: 'audio/aiff',               reliable: false, description: 'AIFF (limited browser support)' },
  caf:  { mime: 'audio/x-caf',              reliable: false, description: 'Core Audio Format (iOS, limited browser support)' },
};

// Regex that matches any supported audio extension at the end of a filename
// or just before a query string / fragment. Built from AUDIO_FORMATS keys
// so adding a format only requires editing the map above.
export const AUDIO_EXT_REGEX = new RegExp(
  '\\.(' + Object.keys(AUDIO_FORMATS).join('|').replace(/\./g, '\\.') + ')(?:$|[?&#])',
  'i'
);

// Lookup helper. Returns { mime, reliable, description, ext } or a fallback.
export const getAudioFormat = (filenameOrPath) => {
  if (!filenameOrPath) return null;
  const match = String(filenameOrPath).toLowerCase().match(/\.([a-z0-9]+)(?:\?|#|$)/);
  const ext = match?.[1];
  if (!ext) return null;
  const fmt = AUDIO_FORMATS[ext];
  if (!fmt) {
    return { ext, mime: 'audio/mpeg', reliable: false, description: `Unknown format (.${ext})` };
  }
  return { ext, ...fmt };
};

// Convenience: just the MIME type, with a sensible fallback.
export const getMimeType = (filenameOrPath) => {
  const fmt = getAudioFormat(filenameOrPath);
  return fmt?.mime || 'audio/mpeg';
};

// All supported extensions as a flat list — useful for file picker filters.
export const ALL_AUDIO_EXTENSIONS = Object.keys(AUDIO_FORMATS);
